/**
 * PaymentSection.jsx — Embedded in DealDetail.
 *
 * Buyer: shows payment status.
 *   - Deal value < Rs.50,000  → Razorpay checkout
 *   - Deal value >= Rs.50,000 → NEFT bank transfer form + instructions
 * Bidder: shows payment status (deposited/held/released), download payout slip.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress,
  Divider, Paper, TextField, Typography, Collapse,
} from '@mui/material';
import PaymentIcon       from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DownloadIcon      from '@mui/icons-material/Download';
import InfoOutlinedIcon  from '@mui/icons-material/InfoOutlined';
import { apiClient }     from '../../utils/auth';

const STATUS_LABELS = {
  NOT_INITIATED:    { label: 'Payment not initiated',    color: 'default' },
  ORDER_CREATED:    { label: 'Awaiting verification',    color: 'warning' },
  CAPTURED:         { label: 'Payment received — held',  color: 'info'    },
  RELEASED:         { label: 'Payout released',          color: 'success' },
  DISPUTE_RESOLVED: { label: 'Dispute resolved',         color: 'success' },
  REFUNDED:         { label: 'Refunded',                 color: 'default' },
};

const NEFT_THRESHOLD = 50000;

// Platform bank details for NEFT (shown to buyer)
const PLATFORM_BANK = {
  name:    'CyberShield Exchange Pvt Ltd',
  account: '1234567890',
  ifsc:    'HDFC0001234',
  bank:    'HDFC Bank',
  branch:  'Cyber City, Gurugram',
};

export default function PaymentSection({ dealId, dealValue, isBuyer, contractCompleted, onPaymentUpdated }) {
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [paying, setPaying]           = useState(false);
  const [error, setError]             = useState('');
  const [neftSuccess, setNeftSuccess] = useState('');

  // NEFT form state
  const [neftForm, setNeftForm] = useState({
    sender_name: '',
    sender_account_number: '',
    sender_ifsc: '',
    sender_bank_name: '',
    utr_number: '',
    amount: dealValue ? String(dealValue) : '',
    transfer_date: '',
    remarks: '',
  });
  const [neftErrors, setNeftErrors] = useState({});
  const [neftSubmitting, setNeftSubmitting] = useState(false);

  const isNeft = Number(dealValue) >= NEFT_THRESHOLD;

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/api/v1/payment/deals/${dealId}/status/`);
      setPaymentData(data);
    } catch { setPaymentData({ payment_status: 'NOT_INITIATED' }); }
    finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // ── Razorpay checkout (for < Rs.50k) ────────────────────────────────────────
  const handlePay = async () => {
    setPaying(true); setError('');
    try {
      const { data } = await apiClient.post(`/api/v1/payment/deals/${dealId}/initiate/`);
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        const options = {
          key: data.razorpay_key_id,
          amount: data.amount,
          currency: data.currency,
          order_id: data.razorpay_order_id,
          name: 'CyberShield Exchange',
          description: `Deal #${dealId} Payment`,
          handler: async (response) => {
            try {
              await apiClient.post(`/api/v1/payment/deals/${dealId}/verify/`, {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_signature:  response.razorpay_signature,
              });
            } catch { /* webhook fallback */ }
            await fetchStatus();
            onPaymentUpdated?.();
            setPaying(false);
          },
          modal: { ondismiss: () => setPaying(false) },
        };
        new window.Razorpay(options).open();
      };
      document.body.appendChild(script);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Payment initiation failed.');
      setPaying(false);
    }
  };

  // ── NEFT form submission ──────────────────────────────────────────────────────
  const validateNeft = () => {
    const errs = {};
    if (!neftForm.sender_name.trim())            errs.sender_name = 'Account holder name required.';
    if (!neftForm.sender_account_number.trim())  errs.sender_account_number = 'Account number required.';
    if (!neftForm.sender_ifsc.trim())            errs.sender_ifsc = 'IFSC code required.';
    else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/i.test(neftForm.sender_ifsc.trim()))
                                                 errs.sender_ifsc = 'Invalid IFSC format (e.g. HDFC0001234).';
    if (!neftForm.sender_bank_name.trim())       errs.sender_bank_name = 'Bank name required.';
    if (!neftForm.utr_number.trim())             errs.utr_number = 'UTR / reference number required.';
    if (!neftForm.amount.trim())                 errs.amount = 'Transfer amount required.';
    return errs;
  };

  const handleNeftChange = (e) => {
    const { name, value } = e.target;
    setNeftForm(prev => ({ ...prev, [name]: value }));
    setNeftErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleNeftSubmit = async () => {
    const errs = validateNeft();
    if (Object.keys(errs).length) { setNeftErrors(errs); return; }
    setNeftSubmitting(true); setError(''); setNeftSuccess('');
    try {
      const { data } = await apiClient.post(`/api/v1/payment/deals/${dealId}/neft/`, {
        ...neftForm,
        sender_ifsc: neftForm.sender_ifsc.toUpperCase(),
        utr_number: neftForm.utr_number.toUpperCase(),
      });
      setNeftSuccess(data.message || 'NEFT details submitted. Admin will verify your transfer.');
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to submit NEFT details.');
    } finally {
      setNeftSubmitting(false);
    }
  };

  // ── Download slip ──────────────────────────────────────────────────────────────
  const downloadSlip = async (type) => {
    try {
      const resp = await apiClient.get(`/api/v1/payment/deals/${dealId}/slip/${type}/`, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(resp.data);
      link.download = `${type}_slip_deal_${dealId}.pdf`;
      link.click();
    } catch { setError('Could not download slip.'); }
  };

  if (loading) return <CircularProgress size={20} />;

  const ps = paymentData?.payment_status || 'NOT_INITIATED';
  const statusInfo = STATUS_LABELS[ps] || { label: ps, color: 'default' };
  const dealValueNum = Number(dealValue);
  const neftAlreadySubmitted = ps === 'ORDER_CREATED' && isNeft;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {isNeft
          ? <AccountBalanceIcon color="primary" fontSize="small" />
          : <PaymentIcon color="primary" fontSize="small" />
        }
        <Typography variant="subtitle1" fontWeight={600}>Payment</Typography>
        {isNeft && (
          <Chip label="NEFT / RTGS" size="small" color="warning" sx={{ fontSize: 11, ml: 0.5 }} />
        )}
        <Chip label={statusInfo.label} color={statusInfo.color} size="small" sx={{ ml: 'auto' }} />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Deal Value: <strong>Rs.{dealValueNum.toLocaleString('en-IN')}</strong>
        {' '}· Platform fee: 5% · Net to bidder: Rs.{(dealValueNum * 0.95).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
      </Typography>

      {error    && <Alert severity="error"   sx={{ mb: 1.5 }}>{error}</Alert>}
      {neftSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>{neftSuccess}</Alert>}

      {/* ──────────────── BUYER ACTIONS ──────────────── */}
      {isBuyer && ps === 'NOT_INITIATED' && contractCompleted && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Contract is signed. Please complete payment to allow the bidder to begin work.
        </Alert>
      )}

      {/* Razorpay — for < Rs.50,000 */}
      {isBuyer && !isNeft && ps === 'NOT_INITIATED' && contractCompleted && (
        <Button variant="contained" startIcon={<PaymentIcon />} onClick={handlePay} disabled={paying} id="btn-razorpay-pay">
          {paying ? <CircularProgress size={20} color="inherit" /> : `Pay Rs.${dealValueNum.toLocaleString('en-IN')}`}
        </Button>
      )}
      {isBuyer && !isNeft && ps === 'ORDER_CREATED' && (
        <Button variant="outlined" onClick={handlePay} disabled={paying} id="btn-complete-payment">
          {paying ? <CircularProgress size={20} color="inherit" /> : 'Complete Payment'}
        </Button>
      )}

      {/* NEFT — for >= Rs.50,000 */}
      {isBuyer && isNeft && ps === 'NOT_INITIATED' && contractCompleted && (
        <Box>
          {/* Platform bank details */}
          <Paper
            variant="outlined"
            sx={{
              p: 2, mb: 2,
              background: 'rgba(99,102,241,0.06)',
              borderColor: 'rgba(99,102,241,0.25)',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <InfoOutlinedIcon sx={{ color: '#818cf8', fontSize: 18 }} />
              <Typography variant="subtitle2" color="#818cf8" fontWeight={700}>
                NEFT / RTGS Payment Instructions
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              For deals ≥ Rs.50,000, please transfer funds via NEFT or RTGS to the following platform escrow account, then fill in the details below:
            </Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              {[
                ['Account Name',   PLATFORM_BANK.name],
                ['Account Number', PLATFORM_BANK.account],
                ['IFSC Code',      PLATFORM_BANK.ifsc],
                ['Bank',           PLATFORM_BANK.bank],
                ['Branch',         PLATFORM_BANK.branch],
                ['Amount',         `Rs.${dealValueNum.toLocaleString('en-IN')}`],
                ['Reference',      `DEAL-${dealId}`],
              ].map(([label, val]) => (
                <Box component="tr" key={label}>
                  <Box component="td" sx={{ py: 0.4, pr: 2, color: 'text.secondary', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {label}
                  </Box>
                  <Box component="td" sx={{ py: 0.4, fontSize: 13, color: 'text.primary', fontWeight: 500 }}>
                    {val}
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* NEFT details form */}
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            Step 2 — Submit your transfer details for admin verification:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField
                label="Account Holder Name *"
                name="sender_name"
                value={neftForm.sender_name}
                onChange={handleNeftChange}
                size="small"
                error={!!neftErrors.sender_name}
                helperText={neftErrors.sender_name}
                id="neft-sender-name"
              />
              <TextField
                label="Your Bank Name *"
                name="sender_bank_name"
                value={neftForm.sender_bank_name}
                onChange={handleNeftChange}
                size="small"
                error={!!neftErrors.sender_bank_name}
                helperText={neftErrors.sender_bank_name}
                id="neft-sender-bank"
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField
                label="Your Account Number *"
                name="sender_account_number"
                value={neftForm.sender_account_number}
                onChange={handleNeftChange}
                size="small"
                error={!!neftErrors.sender_account_number}
                helperText={neftErrors.sender_account_number}
                id="neft-sender-account"
              />
              <TextField
                label="Your IFSC Code *"
                name="sender_ifsc"
                value={neftForm.sender_ifsc}
                onChange={(e) => handleNeftChange({ target: { name: 'sender_ifsc', value: e.target.value.toUpperCase() } })}
                size="small"
                placeholder="e.g. HDFC0001234"
                error={!!neftErrors.sender_ifsc}
                helperText={neftErrors.sender_ifsc}
                id="neft-sender-ifsc"
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField
                label="UTR / Transaction Reference *"
                name="utr_number"
                value={neftForm.utr_number}
                onChange={(e) => handleNeftChange({ target: { name: 'utr_number', value: e.target.value.toUpperCase() } })}
                size="small"
                placeholder="e.g. HDFC123456789012"
                error={!!neftErrors.utr_number}
                helperText={neftErrors.utr_number || 'Get this from your bank statement or net banking'}
                id="neft-utr"
              />
              <TextField
                label="Transfer Amount (Rs.) *"
                name="amount"
                type="number"
                value={neftForm.amount}
                disabled={true}
                size="small"
                error={!!neftErrors.amount}
                helperText={neftErrors.amount}
                id="neft-amount"
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField
                label="Transfer Date"
                name="transfer_date"
                type="date"
                value={neftForm.transfer_date}
                onChange={handleNeftChange}
                size="small"
                InputLabelProps={{ shrink: true }}
                id="neft-date"
              />
              <TextField
                label="Remarks (optional)"
                name="remarks"
                value={neftForm.remarks}
                onChange={handleNeftChange}
                size="small"
                id="neft-remarks"
              />
            </Box>

            <Button
              variant="contained"
              startIcon={neftSubmitting ? <CircularProgress size={16} color="inherit" /> : <AccountBalanceIcon />}
              onClick={handleNeftSubmit}
              disabled={neftSubmitting}
              id="btn-submit-neft"
              sx={{ alignSelf: 'flex-start', px: 3 }}
            >
              {neftSubmitting ? 'Submitting…' : 'Submit NEFT Details'}
            </Button>
          </Box>
        </Box>
      )}

      {/* NEFT — already submitted, waiting admin verification */}
      {isBuyer && isNeft && ps === 'ORDER_CREATED' && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          ⏳ Your NEFT transfer details have been submitted. An admin will verify the transfer and activate the deal shortly.
          You will receive a notification once verified.
        </Alert>
      )}

      {/* Payment confirmed */}
      {isBuyer && ['CAPTURED', 'RELEASED', 'DISPUTE_RESOLVED'].includes(ps) && (
        <>
          <Alert severity="success" sx={{ mb: 1.5 }}>
            ✅ Payment of Rs.{dealValueNum.toLocaleString('en-IN')} has been {isNeft ? 'verified via NEFT' : 'received via Razorpay'} successfully.
          </Alert>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => downloadSlip('buyer')}>
            Download Payment Slip
          </Button>
        </>
      )}
      {isBuyer && ps === 'CAPTURED' && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Funds are held by the platform. They will be released to the bidder after the 2-day retention period with no active disputes.
        </Typography>
      )}

      {/* ──────────────── BIDDER ACTIONS ──────────────── */}
      {!isBuyer && ps === 'NOT_INITIATED' && (
        <Alert severity="warning">
          Waiting for buyer to {isNeft ? 'complete NEFT transfer' : 'deposit payment'} before you can start work.
        </Alert>
      )}
      {!isBuyer && ps === 'ORDER_CREATED' && (
        <Alert severity="info">
          {isNeft
            ? '⏳ Buyer has submitted NEFT transfer details. Awaiting admin verification.'
            : '⏳ Buyer started payment but it is not confirmed yet. You cannot submit work until payment is captured.'}
        </Alert>
      )}
      {!isBuyer && ps === 'CAPTURED' && (
        <Alert severity="success">
          ✅ Payment of Rs.{dealValueNum.toLocaleString('en-IN')} has been received. You can now start work.
        </Alert>
      )}
      {!isBuyer && ['RELEASED', 'DISPUTE_RESOLVED'].includes(ps) && (
        <>
          <Alert severity="success" sx={{ mb: 1 }}>
            Your payout has been processed. Net amount: Rs.{(dealValueNum * 0.95).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </Alert>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => downloadSlip('bidder')}>
            Download Payout Slip
          </Button>
        </>
      )}
    </Paper>
  );
}
